
import { fun } from '@/lib/request';
import { api } from '@/lib/env';
import { StateSlice } from '@/stores/appStore';
import { snakeToCamel } from '@/lib/utils';
import toast from 'react-hot-toast';

export type FeatureGuide = {
  url: string;
  caption: string;
  description: string;
};

export type FeaturesGuides = {
  type: 'features',
  value: FeatureGuide[],
};

export type GiftGuides = {
  type: 'gift',
  value: {
    url: string;
  },
}

export type CouponsGuides = {
  type: 'coupons',
  value: {
    url: string;
  },
}

export type AiColChargeGuides = {
  type: 'ai_col_charge',
  value: {
    url: string;
    height: number;
    width: number;
    description: string;
  },
}

export type RechargeGuides = {
  type: 'recharge',
  value: {
    url: string;
  },
}

// 直接出来支付弹窗
export type BuyingGuides = {
  type: 'buying',
  value: {
  },
}

// 帮助弹窗
export type HelpGuides = {
  type: 'help',
  value: {
    title?: string;
    icon?: string;
    watermarkImage?: string;
    reward?: {
      prefix?: string;
      highlight?: string;
      description?: string;
    };
    sections?: {
      title?: string;
      content?: string;
    }[];
    // Legacy fields kept for existing paperlist_help payloads.
    image?: string;
    subtitle?: string;
    content?: string;
    links: {
      label: string;
      url: string;
    }[];
  },
}


export type Guides = FeaturesGuides | GiftGuides | CouponsGuides | RechargeGuides | AiColChargeGuides | BuyingGuides | HelpGuides;


export type TouchpointSlot = 'modal' | 'footerAd' | 'sideTip';

export type TouchpointCta = {
  action?: string;
  tab?: string;
};

export type TouchpointContent = Guides & {
  providerType?: string;
  rules?: {
    rule: string;
    value: unknown;
  }[];
  rulesExpr?: unknown;
};

export type TouchpointPayload = {
  content?: TouchpointContent;
  cta?: TouchpointCta;
  bonusBeans?: number;
  recoverableAmount?: number;
  endTime?: number;
  remainingSeconds?: number;
  expireTime?: number;
  daysLeft?: number;
  [key: string]: unknown;
};

export type TouchpointItem = {
  id: string;
  type: string;
  slot: TouchpointSlot;
  priority: number;
  payload: TouchpointPayload;
  dismissible: boolean;
  dismissToken: string;
};

export type TouchpointSlots = Partial<Record<TouchpointSlot, TouchpointItem>>;

type TouchpointsData = {
  items?: TouchpointItem[];
};

const touchpointSlots: TouchpointSlot[] = ['modal', 'footerAd', 'sideTip'];
const touchpointsInFlightByPage = new Map<string, Promise<void>>();

function getTouchpointsInFlightKey(scope: 'page' | 'global', page: string) {
  return `${scope}:${page}`;
}

export function isTouchpointSlot(slot: string): slot is TouchpointSlot {
  return touchpointSlots.includes(slot as TouchpointSlot);
}

export function groupTouchpointsBySlot(items: TouchpointItem[]) {
  return items.reduce<TouchpointSlots>((slots, item) => {
    if (!isTouchpointSlot(item.slot)) {
      console.warn('Unknown touchpoint slot:', item.slot, item);
      return slots;
    }
    if (!slots[item.slot]) {
      slots[item.slot] = item;
    }
    return slots;
  }, {});
}

export function getTouchpointGuides(item?: TouchpointItem | null): Guides | null {
  const content = item?.payload?.content;
  if (!content?.type) {
    return null;
  }
  if ([
    'features',
    'gift',
    'coupons',
    'ai_col_charge',
    'recharge',
    'buying',
    'help',
  ].includes(content.type)) {
    return content as Guides;
  }
  return null;
}

export function isTouchpointContentType(item: TouchpointItem | null | undefined, type: Guides['type']) {
  return getTouchpointGuides(item)?.type === type;
}

function removeTouchpointItem(items: TouchpointItem[], item: TouchpointItem) {
  return items.filter((touchpoint) => touchpoint.id !== item.id);
}

async function requestTouchpoints(page: string) {
  const formData = new FormData();
  formData.append('page', page);
  const { data, code } = (await fun.post(api('/common/touchpoints', true), formData)).data;
  if (code === 0) {
    const row = snakeToCamel<TouchpointsData>(data || {});
    return {
      code,
      items: row.items || [],
    };
  }
  return {
    code,
    items: [] as TouchpointItem[],
  };
}

export type GuideSlice = {
  touchpointItems: TouchpointItem[];
  touchpointSlots: TouchpointSlots;
  globalTouchpointItems: TouchpointItem[];
  globalTouchpointSlots: TouchpointSlots;
  currentTouchpointsPage: string;
  fetchTouchpoints: (page: string) => Promise<void>;
  fetchGlobalTouchpoints: () => Promise<void>;
  dismissTouchpoint: (
    item: TouchpointItem,
    options?: {
      refresh?: boolean;
    }
  ) => Promise<boolean>;
  clearTouchpoints: () => void;
}

export const createGuideSlice: StateSlice<GuideSlice> = ((set, get) => ({
  touchpointItems: [],
  touchpointSlots: {},
  globalTouchpointItems: [],
  globalTouchpointSlots: {},
  currentTouchpointsPage: '',
  fetchTouchpoints: async (page) => {
    if (!page) {
      return;
    }
    const requestKey = getTouchpointsInFlightKey('page', page);
    const inFlightRequest = touchpointsInFlightByPage.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request = (async () => {
      const { code, items } = await requestTouchpoints(page);
      if (code === 0) {
        set((state) => {
          state.touchpointItems = items;
          state.touchpointSlots = groupTouchpointsBySlot(items);
          state.currentTouchpointsPage = page;
        });
        return;
      }
      if (code === 4001) {
        get().clearTouchpoints();
      }
    })().catch((error) => {
      console.error('Error fetch touchpoints:', error);
    }).finally(() => {
      touchpointsInFlightByPage.delete(requestKey);
    });

    touchpointsInFlightByPage.set(requestKey, request);
    return request;
  },
  fetchGlobalTouchpoints: async () => {
    const page = '*';
    const requestKey = getTouchpointsInFlightKey('global', page);
    const inFlightRequest = touchpointsInFlightByPage.get(requestKey);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request = (async () => {
      const { code, items } = await requestTouchpoints(page);
      if (code === 0) {
        set((state) => {
          state.globalTouchpointItems = items;
          state.globalTouchpointSlots = groupTouchpointsBySlot(items);
        });
        return;
      }
      if (code === 4001) {
        get().clearTouchpoints();
      }
    })().catch((error) => {
      console.error('Error fetch global touchpoints:', error);
    }).finally(() => {
      touchpointsInFlightByPage.delete(requestKey);
    });

    touchpointsInFlightByPage.set(requestKey, request);
    return request;
  },
  dismissTouchpoint: async (item, options = {}) => {
    const { refresh = false } = options;
    if (!item?.dismissToken) {
      return false;
    }
    try {
      const formData = new FormData();
      formData.append('dismiss_token', item.dismissToken);
      const { code } = (await fun.post(api('/common/touchpointsDismiss', true), formData)).data;
      if (code === 0) {
        const page = get().currentTouchpointsPage;
        const shouldRefreshGlobal = get().globalTouchpointItems.some((touchpoint) => touchpoint.id === item.id);
        set((state) => {
          const items = removeTouchpointItem(state.touchpointItems, item);
          const globalItems = removeTouchpointItem(state.globalTouchpointItems, item);
          state.touchpointItems = items;
          state.touchpointSlots = groupTouchpointsBySlot(items);
          state.globalTouchpointItems = globalItems;
          state.globalTouchpointSlots = groupTouchpointsBySlot(globalItems);
        });
        if (refresh && page) {
          await get().fetchTouchpoints(page);
        }
        if (refresh && shouldRefreshGlobal) {
          await get().fetchGlobalTouchpoints();
        }
        return true;
      }
      if (code === 1001) {
        toast.error('当前提示已失效');
        set((state) => {
          const items = removeTouchpointItem(state.touchpointItems, item);
          const globalItems = removeTouchpointItem(state.globalTouchpointItems, item);
          state.touchpointItems = items;
          state.touchpointSlots = groupTouchpointsBySlot(items);
          state.globalTouchpointItems = globalItems;
          state.globalTouchpointSlots = groupTouchpointsBySlot(globalItems);
        });
        return false;
      }
      toast.error('关闭失败，请重试');
      return false;
    } catch (error) {
      console.error('Error dismiss touchpoint:', error);
      toast.error('关闭失败，请重试');
      return false;
    }
  },
  clearTouchpoints: () => {
    set((state) => {
      state.touchpointItems = [];
      state.touchpointSlots = {};
      state.globalTouchpointItems = [];
      state.globalTouchpointSlots = {};
      state.currentTouchpointsPage = '';
    });
  },
}));
